import React, { useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Chip, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { AHA_TOKENS as T, FOCUS_RING } from "./ahaTokens";
import { courseFamily, groupCourses, isCourseAvailable } from "./ahaCourses";

/** Why a listed course can't be chosen, in one plain sentence. */
export function unavailableReason(product) {
  if (isCourseAvailable(product)) return null;
  if (product?.environment?.verified) return "Retired edition. Choose a newer edition if one is listed.";
  return "Not verified for your center yet. Your admin can request it from the AHA.";
}

const usesModules = (product) => (product?.modules || []).length > 0;
const description = (product) => (usesModules(product) ? "Uses AHA modules" : "No modules to add");
const edTag = { fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5, px: "6px", py: "2px", borderRadius: "4px",
  border: `1px solid ${T.hairlineCool}`, color: T.ink, lineHeight: 1.3 };

/**
 * The AHA course picker: a searchable, grouped combobox shown inline (no dialog). Courses that can't be sent stay
 * listed with a one-line reason instead of vanishing. `selectedKey` is the saved course; `onSelect(product)` is
 * called for an available course.
 */
export default function AhaCoursePicker({ products = [], selectedKey = null, onSelect, readOnly = false, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);
  const inputRef = useRef(null);

  const q = query.trim().toLowerCase();
  const groups = useMemo(() => groupCourses(products).map((g) => ({
    family: g.family,
    products: g.products.filter((p) => !q || `${g.family} ${p.name} ${p.edition || ""}`.toLowerCase().includes(q)),
  })).filter((g) => g.products.length > 0), [products, q]);
  const flat = groups.flatMap((g) => g.products);
  const selected = products.find((p) => p.key === selectedKey) || null;

  const focusInput = () => setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  const openList = (activeKey = null) => { setOpen(true); setActive(activeKey); focusInput(); };
  const close = () => { setOpen(false); setQuery(""); };
  const choose = (product) => {
    if (!isCourseAvailable(product)) { setActive(product.key); return; }
    close();
    if (onSelect) onSelect(product);
  };
  const onKeyDown = (e) => {
    const index = flat.findIndex((p) => p.key === active);
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(flat[Math.min(flat.length - 1, index + 1)]?.key ?? null); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(flat[Math.max(0, index - 1)]?.key ?? null); }
    else if (e.key === "Enter" && index >= 0) { e.preventDefault(); choose(flat[index]); }
    else if (e.key === "Escape") close();
  };

  if (selected && !open) {
    return (
      <Box data-testid="aha-course-selected" sx={{ display: "flex", alignItems: "center", gap: "14px", p: "14px 16px",
        border: `1px solid ${T.hairlineCool}`, borderRadius: "10px", bgcolor: "#fff", maxWidth: 560 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "8px", display: "grid", placeItems: "center", flexShrink: 0,
          bgcolor: "rgba(136,14,79,.08)", color: T.aha }}>
          <CreditCardIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography component="span" sx={{ fontSize: 15, fontWeight: 600 }}>{selected.name}</Typography>
            {selected.edition && <Box component="span" sx={edTag}>{selected.edition}</Box>}
          </Box>
          <Typography sx={{ fontSize: 13, color: T.muted }}>{`${courseFamily(selected.key)} · ${description(selected)}`}</Typography>
        </Box>
        {!readOnly && !disabled && (
          <ButtonBase onClick={() => openList(selected.key)}
            sx={{ fontSize: 14, fontWeight: 600, color: T.violet, "&:hover": { color: T.ink }, "&.Mui-focusVisible": FOCUS_RING }}>
            Change
          </ButtonBase>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 560 }}>
      <Typography component="label" htmlFor="aha-course-input" sx={{ display: "block", fontSize: 13, fontWeight: 600, mb: "6px" }}>
        AHA course
      </Typography>
      {!open ? (
        <ButtonBase disabled={disabled || readOnly} onClick={() => openList()} aria-haspopup="listbox"
          sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", height: 46, px: "14px", textAlign: "left",
            border: `1px solid ${T.hairlineCool}`, borderRadius: "6px", bgcolor: "#fff", justifyContent: "flex-start",
            "&.Mui-focusVisible": FOCUS_RING, "&.Mui-disabled": { opacity: 0.6 } }}>
          <SearchIcon sx={{ fontSize: 18, color: T.muted }} />
          <Box component="span" sx={{ flex: 1, color: T.muted, fontSize: 15 }}>Search AHA courses, e.g. Heartsaver</Box>
          <ExpandMoreIcon sx={{ fontSize: 20 }} />
        </ButtonBase>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", height: 46, px: "14px", bgcolor: "#fff",
            border: `1px solid ${T.violet}`, borderRadius: "6px", boxShadow: `0 0 0 3px ${T.ring}` }}>
            <SearchIcon sx={{ fontSize: 18, color: T.muted }} />
            <Box component="input" id="aha-course-input" ref={inputRef} role="combobox" aria-expanded="true"
              aria-controls="aha-course-list" aria-activedescendant={active ? `aha-opt-${active}` : undefined}
              value={query} placeholder="Search AHA courses, e.g. Heartsaver"
              onChange={(e) => setQuery(e.target.value)} onKeyDown={onKeyDown}
              sx={{ flex: 1, border: 0, outline: 0, font: "inherit", fontSize: 15, bgcolor: "transparent", color: "inherit" }} />
            <ButtonBase aria-label="Close" onClick={close} sx={{ color: T.muted, "&.Mui-focusVisible": FOCUS_RING }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </ButtonBase>
          </Box>
          <Box id="aha-course-list" role="listbox" sx={{ mt: "6px", p: "6px", maxHeight: 440, overflow: "auto", bgcolor: "#fff",
            border: `1px solid ${T.hairlineCloud}`, borderRadius: "10px", boxShadow: T.popover }}>
            {groups.length === 0 && (
              <Typography sx={{ p: 2, fontSize: 14, color: T.muted }}>{`No AHA courses match “${query}”.`}</Typography>
            )}
            {groups.map((g) => (
              <Box key={g.family} role="group" aria-label={g.family}>
                <Box sx={{ display: "flex", justifyContent: "space-between", p: "12px 10px 6px", fontSize: 10, fontWeight: 600,
                  letterSpacing: ".25px", textTransform: "uppercase", color: T.muted }}>
                  <span>{g.family}</span>
                  <span>{`${g.products.filter(isCourseAvailable).length} available`}</span>
                </Box>
                {g.products.map((p) => {
                  const reason = unavailableReason(p);
                  return (
                    <Box key={p.key} id={`aha-opt-${p.key}`} role="option" aria-selected={p.key === selectedKey}
                      aria-disabled={reason ? "true" : "false"} onClick={() => choose(p)} onMouseEnter={() => setActive(p.key)}
                      sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: "4px 12px", p: "10px", borderRadius: "8px",
                        alignItems: "center", cursor: reason ? "not-allowed" : "pointer",
                        bgcolor: p.key === selectedKey ? "rgba(106,95,193,.08)" : active === p.key ? T.press : "transparent" }}>
                      <Typography component="span" sx={{ fontSize: 15, fontWeight: 500, color: reason ? T.muted : "inherit" }}>{p.name}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {reason && <Chip size="small" icon={<LockOutlinedIcon />} label="Unavailable"
                          sx={{ height: 22, fontSize: 11.5, fontWeight: 600, color: T.muted, bgcolor: "rgba(121,98,140,.1)" }} />}
                        {p.edition && <Box component="span" sx={edTag}>{p.edition}</Box>}
                        {p.key === selectedKey && <CheckIcon sx={{ fontSize: 18, color: T.success }} />}
                      </Box>
                      {reason ? (
                        <Box sx={{ gridColumn: "1 / -1", display: "flex", gap: "6px", alignItems: "center", fontSize: 13 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: T.muted }} />{reason}
                        </Box>
                      ) : (
                        <Typography sx={{ gridColumn: "1 / -1", fontSize: 13, color: T.muted }}>
                          {`${g.family} course${usesModules(p) ? " · uses modules" : ""}`}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
